
import axios, { AxiosError, AxiosResponse } from 'axios';
import {
  ExternalData,
  ExternalDataMapping,
  ExternalDataSystem,
  ExternalDataTable,
  ExternalDataType,
  FbeCharacteristics,
  FbeCriteria,
  FbeDependency,
  FbeForm,
  FbeFrontline,
  FbeGenerationType,
  FbeLoop,
  FbeQuestion,
  FbeQuestionAnswer,
  FbeQuestionType,
  FbeReference,
  FbeSection,
  FbeSectionDisplayType,
  FbeSourceDefinitionFormat,
  FbeSourceDestination,
  FbeTask,
  FbeType,
  FbeVariableTranslations,
  IesMergeMetaData,
  InjectTime,
  LocalisedText,
} from './fbeTypes';

export enum API_TYPE {
  FORM_BUILDER,
}

export enum Constants {
  FORM_GLOBAL = 'form_global',
  LOCKED = 'locked',
  EXTENDED = 'extended',
  DRAFT = 'draft',
  LIVE = 'live',
  LOCAL = 'local',
  DEACTIVATED = 'deactivated',
}

const FORM_BUILDER_API_URL = process.env.VITE_FORM_BUILDER_API_URL || '';

const DUMMY_DATE = '2000-01-01 01:02:03';

const getBaseUrl = (api: API_TYPE) => {
  switch (api) {
    case API_TYPE.FORM_BUILDER:
      return FORM_BUILDER_API_URL;
  }
};

export const fbGetForms = async (
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<FbeForm[]> => {
  try {
    const url = 'forms';
    const baseURL = getBaseUrl(api);
    const { data } = await axios.get(`${baseURL}/${url}`, {
      headers: {
        Authorization: `${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    return data;
  } catch (error) {
    console.error(error);
    return Promise.resolve([]);
  }
};

export type ExtendType = {
  referenceId: string;
}

export const fbDuplicateForm = async (
  pk: string,
  sk: string,
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<ExtendType | null> => {
  try {
    const url = `forms/duplicate/${pk}%23${sk}`;
    const baseURL = getBaseUrl(api);
    const { data } = await axios.get(`${baseURL}/${url}`, {
      headers: {
        Authorization: `${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    return data;
  } catch (error) {
    console.error(error);
    return Promise.resolve(null);
  }
};

export const fbExtendForm = async (
  pk: string,
  sk: string,
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<ExtendType | null> => {
  try {
    const url = `forms/extend/${pk}%23${sk}`;
    const baseURL = getBaseUrl(api);
    const { data } = await axios.get(`${baseURL}/${url}`, {
      headers: {
        Authorization: `${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    return data;
  } catch (error) {
    console.error(error);
    return Promise.resolve(null);
  }
};


export const fbGetForm = async (
  pk: string,
  sk: string,
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<FbeForm | null> => {
  try {
    const url = `forms/${pk}%23${sk}`;
    const baseURL = getBaseUrl(api);
    const { data } = await axios.get(`${baseURL}/${url}`, {
      headers: {
        Authorization: `${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    return data['form'];
  } catch (error) {
    console.error(error);
    return Promise.resolve(null);
  }
};

// export const fbGetExportLocalization =  async (
//   referenceId: string,
//   accessToken: string,
//   api: API_TYPE = API_TYPE.FORM_BUILDER
// ): Promise<AxiosResponse | null> => {
//   try {
//     const url = `forms/${referenceId}/export/localization`;
//     const baseURL = getBaseUrl(api);
//     const response = await axios.get(`${baseURL}/${url}`, {
//       headers: {
//         Authorization: `${accessToken}`,
//         'Content-Type': 'application/json',
//       },
//     });
//     return response;
//   } catch (error) {
//     console.error(error);
//     return Promise.resolve(null);
//   }
// };

export const fbGetExportLocalization = async (
  referenceId: string,
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<AxiosResponse | null> => {
  try {
    const url = `forms/${referenceId}/export/localization`;
    const baseURL = getBaseUrl(api);
    const response = await axios.get(`${baseURL}/${url}`, {
      headers: {
        Authorization: `${accessToken}`,
        'Accept': 'text/csv; charset=utf-8',
      },
    });
    return response;
  } catch (error) {
    console.error(error);
    return Promise.resolve(null);
  }
};

export enum FileType {
  FORM = 'form',
  LOCALIZATION = 'localization',
  QUESTIONS = 'questions',
  NONE = 'none',
}

export const fbPostImport = async (
  fileType: FileType,
  referenceId: string,
  data: FormData,
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<AxiosResponse | null> => {
  try {
    let contentType = 'application/json';
    if(fileType === FileType.LOCALIZATION || fileType === FileType.QUESTIONS){
      contentType = 'text/csv; charset=utf-8';
    }
    const file = data.get('file') as File;
    const fileContent = await file.text();  // This should preserve UTF-8

    const url = `forms/${referenceId}/import/${fileType}`;
    const baseURL = getBaseUrl(api);
    const response = await axios.post(`${baseURL}/${url}`,
      fileContent,
      {
        headers: {
          Authorization: `${accessToken}`,
          'Content-Type': contentType,
        }
      },
    );

    return response;
  } catch (error) {
    console.error(error);
    const detailResult = {
      data: ((error as AxiosError).response as AxiosResponse).data,
      status: ((error as AxiosError).response as AxiosResponse).status
    } as AxiosResponse;
    return Promise.resolve(detailResult);
  }
};

export const fbGetExport = async (
  fileType: FileType,
  referenceId: string,
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<AxiosResponse | null> => {
  try {
    const url = `forms/${referenceId}/export/${fileType}`;
    const baseURL = getBaseUrl(api);
    const headers: Record<string, string> = {
      Authorization: `${accessToken}`,
      'Content-Type': 'application/json',
    };
    // Request UTF-8 encoding for CSV files
    if(fileType === FileType.LOCALIZATION || fileType === FileType.QUESTIONS){
      headers['Accept'] = 'text/csv; charset=utf-8';
    }
    const response = await axios.get(`${baseURL}/${url}`, { headers });
    return response;
  } catch (error) {
    console.error(error);
    return Promise.resolve(null);
  }
};

export const fbGetParentForm = async (
  formId: string,
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<FbeForm | null> => {
  try {
    const url = `forms/parent/${formId}`;
    const baseURL = getBaseUrl(api);
    const { data } = await axios.get(`${baseURL}/${url}`, {
      headers: {
        Authorization: `${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    return data['form'];
  } catch (error) {
    console.error(error);
    return Promise.resolve(null);
  }
};

const adjustCopyForm = (form: FbeForm, type: FbeType): FbeForm => {
  const newForm = JSON.parse(JSON.stringify(form));
  newForm.pk = '';
  newForm.sk = '';
  newForm.type = type;
  newForm.version = 1;

  switch (type) {
    case FbeType.MERGE:
    case FbeType.REPLACE:
      newForm.order = form.order + 10;
      break;
    default:
      break;
  }

  return newForm;
};

export const fbGetCopyForm = async (
  pk: string,
  sk: string,
  type: FbeType,
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<FbeForm | null> => {
  const originalForm = await fbGetForm(pk, sk, accessToken, api);
  if (originalForm) {
    return adjustCopyForm(originalForm, type);
  } else {
    return null;
  }
};

const prepareForm = (isPost: boolean, form: FbeForm): FbeForm => {
  const preparedForm = {
    ...form,
    // dummy values
    createdAt: DUMMY_DATE,
    createdBy: 'x',
    createdByName: 'x',
    modifiedAt: DUMMY_DATE,
    modifiedBy: 'x',
    modifiedByName: 'x',
    // default values
    format: FbeSourceDefinitionFormat.IES,
    generationType: FbeGenerationType.AUTO,
    sourceDestination: FbeSourceDestination.IES_FRM,
    version: isPost ? 1 : form.version,
  };

  if (form.externalData) {
    if (form.externalData) {
      const newExternalData = form.externalData.map((externalData, index) => {
        return {
          mapping: externalData.mapping,
          // default values
          injectTime: InjectTime.GENERATION,
          system: ExternalDataSystem.IES,
          type: ExternalDataType.DYNAMO_DB,
          table: index === 0 ? ExternalDataTable.CHARACTERISTICS : ExternalDataTable.NETWORK,
        };
      });
      preparedForm.externalData = newExternalData;
    }
  }

  return preparedForm;
};

export const fbGetFrontline = async (
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<FbeFrontline | null> => {
  try {
    const url = 'frontline';
    const baseURL = getBaseUrl(api);

    const { data } = await axios.get(
      `${baseURL}/${url}`,
      {
        headers: {
          Authorization: `${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return data as FbeFrontline;
  } catch (error) {
    console.error(error);
    return Promise.resolve(null);
  }
};

export const fbPostForm = async (
  form: FbeForm,
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<any | null> => {
  try {
    const url = 'forms';
    const baseURL = getBaseUrl(api);

    const { data } = await axios.post(
      `${baseURL}/${url}`,
      {
        ...prepareForm(true, JSON.parse(JSON.stringify(form)) as FbeForm),
      },
      {
        headers: {
          Authorization: `${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return data;
  } catch (error) {
    console.error(error);
    return Promise.resolve(null);
  }
};

export const fbPutForm = async (
  form: FbeForm,
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<FbeForm | null> => {
  try {
    const url = `forms/${form.pk}%23${form.sk}`;
    const baseURL = getBaseUrl(api);

    const { data } = await axios.put(
      `${baseURL}/${url}`,
      {
        ...prepareForm(false, JSON.parse(JSON.stringify(form)) as FbeForm),
      },
      {
        headers: {
          Authorization: `${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return data as unknown as FbeForm;
  } catch (error) {
    console.error(error);
    return Promise.resolve(null);
  }
};

export const fbDeleteForm = async (
  form: FbeForm,
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<any | null> => {
  try {
    const url = `forms/${form.pk}%23${form.sk}`;
    const baseURL = getBaseUrl(api);

    const { data } = await axios.delete(`${baseURL}/${url}`, {
      headers: {
        Authorization: accessToken,
        'Content-Type': 'application/json',
      },
    });
    return data;
  } catch (error) {
    console.error(error);
    return Promise.resolve(null);
  }
};

export const fbReleaseForm = async (
  form: FbeForm,
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<FbeForm | null> => {
  try {
    const url = `forms/${form.pk}%23${form.sk}/release`;
    const baseURL = getBaseUrl(api);

    const { data } = await axios.post(
      `${baseURL}/${url}`,
      {},
      {
        headers: {
          Authorization: accessToken,
          'Content-Type': 'application/json',
        },
      }
    );
    return data as unknown as FbeForm;
  } catch (error) {
    console.error(error);
    return Promise.resolve(null);
  }
};

export const fbGetSections = async (
  pk: string,
  sk: string,
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<FbeSection[] | null> => {
  try {
    const url = `sections/${pk}%23${sk}`;
    const baseURL = getBaseUrl(api);
    const { data } = await axios.get(`${baseURL}/${url}`, {
      headers: {
        Authorization: `${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    return data;
  } catch (error) {
    console.error(error);
    return Promise.resolve(null);
  }
};

const prepareSection = (
  form: FbeForm,
  section: FbeSection,
  index: number
): FbeSection => {
  const preparedSection = {
    ...section,
    //    pk?: string; // set by caller
    sk: `${index}`,
    modifiedAt: DUMMY_DATE,
    modifiedBy: 'x',
    modifiedByName: 'x',
    internalCode: `${section.pk}_${index}`,
  };

  if (section.questions && section.questions.length > 0) {
    preparedSection.questions = section.questions.sort(
      (a, b) => a.order - b.order
    );
  } else {
    preparedSection.questions = [];
  }

  if(!section.displayType){
    preparedSection.displayType = FbeSectionDisplayType.LIST;
  }

  if (!section.tasks) {
    preparedSection.tasks = [];
  }

  if (!section.mergeMetaData) {
    preparedSection.mergeMetaData = getMergeMetaData(form, section, index);
  }

  return preparedSection;
};

export const fbPutSections = async (
  form: FbeForm,
  sections: FbeSection[],
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<FbeSection[] | null> => {
  const sortedSections = sections.sort((a, b) => a.order - b.order);

  const preparedSections = sortedSections.map((section, index) => {
    return prepareSection(
      form,
      { ...section, pk: `question_group_${form.pk}_${form.sk}` },
      index
    );
  });

  try {
    const url = `sections/${form.pk}%23${form.sk}`;
    const baseURL = getBaseUrl(api);

    const { data } = await axios.put(
      `${baseURL}/${url}`,
      [...preparedSections],
      {
        headers: {
          Authorization: `${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return data;
  } catch (error) {
    console.error(error);
    return Promise.resolve(null);
  }
};

export const getParentSetId = (form: FbeForm): string => {
  return form.questionSetIdRange || '';
};

export const getNextQuestionSetId = (form: FbeForm, index: number): string => {
  return `${form.questionSetIdRange?.substring(0, 6)}${index < 9 ? '0' : ''}${
    index + 1
  }`;
};

export const getNextQuestionId = (
  form: FbeForm,
  section: FbeSection
): string => {
  return `question_group_${form.pk}_${form.sk}_${section.sk || section.order}_${
    section.questions.length
  }`;
};

export const getTag = (form: FbeForm, section: FbeSection): string => {
  return `tag_${form.sk ? form.sk : 'tbd'}_${section.name[0].text
    .replaceAll(' ', '_')
    .toLowerCase()}_`;
};

export const getMergeMetaData = (
  form: FbeForm,
  newSection: FbeSection,
  index: number
): IesMergeMetaData => {
  return {
    questionSetId: getNextQuestionSetId(form, index),
    parentSetId: getParentSetId(form),
    tacoRole: form.role,
    description: newSection.name,
    tag: getTag(form, newSection),
  };
};

export const fbGetAdminImages = async (
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<string[] | null> => {
  try {
    const url = 'images/admin';
    const baseURL = getBaseUrl(api);
    const { data } = await axios.get(`${baseURL}/${url}`, {
      headers: {
        Authorization: `${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    return data;
  } catch (error) {
    console.error(error);
    return Promise.resolve(null);
  }
};

export const fbGetImages = async (
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<string[] | null> => {
  try {
    const url = 'images';
    const baseURL = getBaseUrl(api);
    const { data } = await axios.get(`${baseURL}/${url}`, {
      headers: {
        Authorization: `${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    return data;
  } catch (error) {
    console.error(error);
    return Promise.resolve(null);
  }
};

const getFilePath = (filePathName: string): string => {
  const parts =
    filePathName.endsWith('/') && filePathName.length > 0
      ? filePathName.substring(0, filePathName.length - 1).split('/')
      : filePathName.split('/');
  return parts.reduce((previous, current, index) => {
    if (index < parts.length - 1) {
      return `${previous}${current}/`;
    } else {
      return previous;
    }
  }, '');
};

export const fbGetImageUrl = async (
  filePathName: string,
  upload: boolean,
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<string | null> => {
  try {
    const url = 'images';
    const baseURL = getBaseUrl(api);

    const parts = filePathName.split('/');
    const filename = parts.length > 1 ? parts[parts.length - 1] : '';
    const path = getFilePath(filePathName);

    const { data } = await axios.post(
      `${baseURL}/${url}`,
      {
        upload,
        filename,
        path,
      },
      {
        headers: {
          Authorization: `${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return data as unknown as string;
  } catch (error) {
    console.error(error);
    return Promise.resolve(null);
  }
};

export const fbDeleteImage = async (
  filePathName: string,
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<string | null> => {
  try {
    const url = 'images';
    const baseURL = getBaseUrl(api);

    const parts = filePathName.split('/');
    const objectname = parts.length > 1 ? parts[parts.length - 1] : '';
    const path = getFilePath(filePathName);

    const { data } = await axios.delete(`${baseURL}/${url}`, {
      headers: {
        Authorization: `${accessToken}`,
        'Content-Type': 'application/json',
      },
      data: {
        objectname,
        path,
      },
    });
    return data as unknown as string;
  } catch (error) {
    console.error(error);
    return Promise.resolve(null);
  }
};

export const fbDeleteImageStructure = async (
  filePathName: string,
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<string | null> => {
  try {
    const url = 'images/structure';
    const baseURL = getBaseUrl(api);

    const parts = filePathName.split('/');
    const objectname = parts.length > 2 ? `${parts[parts.length - 2]}/` : '';
    const path = getFilePath(filePathName);

    const { data } = await axios.delete(`${baseURL}/${url}`, {
      headers: {
        Authorization: `${accessToken}`,
        'Content-Type': 'application/json',
      },
      data: {
        objectname,
        path,
      },
    });
    return data as unknown as string;
  } catch (error) {
    console.error(error);
    return Promise.resolve(null);
  }
};

export const fbCreateImageStructure = async (
  filePathName: string,
  accessToken: string,
  api: API_TYPE = API_TYPE.FORM_BUILDER
): Promise<string | null> => {
  try {
    const url = 'images/structure';
    const baseURL = getBaseUrl(api);

    const parts = filePathName.split('/');
    const foldername = parts.length > 1 ? parts[parts.length - 1] : '';
    const path = getFilePath(filePathName);

    const { data } = await axios.post(
      `${baseURL}/${url}`,
      {
        foldername,
        path,
      },
      {
        headers: {
          Authorization: `${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return data as unknown as string;
  } catch (error) {
    console.error(error);
    return Promise.resolve(null);
  }
};

const isSectionLoopValid = (loop: FbeLoop): boolean => {
  if(!isLocalisationValid(loop.name)){
      return false;
  }

  if(!isValidNumber(loop.total)){
    return false;
  }

  return true;
}

const isDependencyValid = (_external: boolean, dependencies: FbeDependency[]): boolean => {
  if(dependencies.length === 0){
    return true;
  }

  const invalid = dependencies.filter(dependency =>
    !dependency.variable ||
    dependency.variable === '' ||
    !dependency.logicalOperator ||
    `${dependency.logicalOperator}` === '' ||
    !dependency.operator ||
    `${dependency.operator}` === '' ||
    (
      (dependency.compareVariable === undefined || dependency.compareVariable === '') &&
      (dependency.compareValue === undefined || dependency.compareValue === '')
    )
  );

  return invalid.length === 0;
}

const isSectionMergeDataValid = (data: IesMergeMetaData) : boolean => {
  if(!data.questionSetId ||
    data.questionSetId === '' ||
    !data.parentSetId ||
    data.parentSetId === '' ||
    !data.tag ||
    data.tag === '' ||
    !data.tacoRole ||
    data.tacoRole.length === 0 ||
    !isLocalisationValid(data.description)
  ){
    return false;
  }

  return true;
}

const isAnswerValid = (answer: FbeQuestionAnswer): boolean => {
  if(!isLocalisationValid(answer.answer) ||
    !isValidNumber(answer.score)
  ){
    return false;
  }
  return true;
}

const areAnswersValid = (answers: FbeQuestionAnswer[]): boolean => {
  if(answers.length === 0){ return true; }

  const invalid = answers.filter(answer => !isAnswerValid(answer))
  return invalid.length === 0;
}

const isTranslationValid = (translation: FbeVariableTranslations): boolean => {
  if(!translation ||
    !translation.key ||
    translation.key === '' ||
    translation.value === '' ||
    translation.value === undefined
  ){
    return false;
  }
  return true;
}

const areTranslationsValid = (translations: FbeVariableTranslations[]): boolean => {
  if(translations.length === 0){ return true; }

  const invalid = translations.filter(translation => !isTranslationValid(translation))
  return invalid.length === 0;
}

const isReferenceValid = (reference: FbeReference): boolean => {
  if(!reference ||
    !reference.type ||
    !reference.location
  ){
    return false;
  }
  return true;
}

const areReferencesValid = (references: FbeReference[]): boolean => {
  if(references.length === 0){ return true; }

  const invalid = references.filter(reference => !isReferenceValid(reference))
  return invalid.length === 0;
}

export const isQuestionValid = (question: FbeQuestion, fieldsToCheck: string[]): boolean => {
  if(!question){ return false; }

  if(question.additionalInfo && !isLocalisationValid(question.additionalInfo)){
    if(checkField('additionalInfo',fieldsToCheck)){ return false; }
  }

  if(question.answers && !areAnswersValid(question.answers)){
    if(checkField('answers',fieldsToCheck)){ return false; }
  }

  if(question.columnShort && !isLocalisationValid(question.columnShort)){
    if(checkField('columnShort',fieldsToCheck)){ return false; }
  }

  if(!isLocalisationValid(question.question)){
    if(checkField('question',fieldsToCheck)){ return false; }
  }

  if(question.questionType === FbeQuestionType.NUMBER_TABLE && !question.tableType){
    if(checkField('tableType',fieldsToCheck)){ return false; }
  }

  if(question.rowPositionNames && question.rowPositionNames.length > 0){
    const invalid = question.rowPositionNames.filter(rowPositionName =>
      !(isValidNumber(rowPositionName.position) && isLocalisationValid(rowPositionName.name))
    )
    if(invalid.length > 0 && checkField('rowPositionNames',fieldsToCheck)){ return false; }
  }

  if(question.questionShort && !isLocalisationValid(question.questionShort)){
    if(checkField('questionShort',fieldsToCheck)){ return false; }
  }

  if(question.externalDependency && !isDependencyValid(true, question.externalDependency)){
    if(checkField('externalDependency',fieldsToCheck)){ return false; }
  }

  if(question.internalDependency && !isDependencyValid(false, question.internalDependency)){
    if(checkField('internalDependency',fieldsToCheck)){ return false; }
  }

  if(!isValidNumber(question.order)){
    if(checkField('order',fieldsToCheck)){ return false; }
  }

  if(!question.questionType){
    if(checkField('questionType',fieldsToCheck)){ return false; }
  }

  if(question.variableTranslations && !areTranslationsValid(question.variableTranslations)){
    if(checkField('variableTranslations',fieldsToCheck)){ return false; }
  }

  if(question.references && !areReferencesValid(question.references)){
    if(checkField('references',fieldsToCheck)){ return false; }
  }

  return true;
}

const areQuestionsValid = (questions: FbeQuestion[]) : boolean => {
  if(questions.length === 0){ return true; }

  const invalid = questions.filter(question => !isQuestionValid(question,[]));

  return invalid.length === 0;
}

const isTaskValid = (task: FbeTask, fieldsToCheck: string[]): boolean => {
  if(!isLocalisationValid(task.task)){
    if(checkField('task',fieldsToCheck)){ return false; }
  }

  if(!task.taskId){
    if(checkField('taskId',fieldsToCheck)){ return false; }
  }

  if(!task.parentTaskId){
    if(checkField('parentTaskId',fieldsToCheck)){ return false; }
  }

  if(!isValidNumber(task.order)){
    if(checkField('order',fieldsToCheck)){ return false; }
  }

  if(!isValidNumber(task.total)){
    if(checkField('total',fieldsToCheck)){ return false; }
  }

  if(!isValidNumber(task.timeInMinutes)){
    if(checkField('timeInMinutes',fieldsToCheck)){ return false; }
  }


  if(task.externalDependency && !isDependencyValid(true, task.externalDependency)){
    if(checkField('externalDependency',fieldsToCheck)){ return false; }
  }

  if(task.internalDependency && !isDependencyValid(false, task.internalDependency)){
    if(checkField('internalDependency',fieldsToCheck)){ return false; }
  }

  return true;
}

const areTasksValid = (tasks: FbeTask[]) : boolean => {
  if(tasks.length === 0){ return true; }

  const invalid = tasks.filter(task => !isTaskValid(task,[]));

  return invalid.length === 0;

}

const checkField = (fieldName: string, fieldsToCheck: string[]) : boolean => {
  return fieldsToCheck.length === 0 || fieldsToCheck.includes(fieldName);
}

const isLocalisationValid = (field: LocalisedText[]): boolean => {
  if(!field ||
    field.length === 0 ||
    field[0].language === '' ||
    field[0].text === ''){
      return false;
  }
  return true;
}

export const isSectionValid = (section: FbeSection, fieldsToCheck: string[]): boolean => {
  if(!section){ return false };

  if(!isLocalisationValid(section.name)){
      if(checkField('name',fieldsToCheck)){ return false; }
  }

  if(!isValidNumber(section.order)){
    if(checkField('order',fieldsToCheck)){ return false; }
  }

  if(section.loop && !isSectionLoopValid(section.loop)){
    if(checkField('loop',fieldsToCheck)){ return false; }
  }

  if(section.externalDependency && !isDependencyValid(true, section.externalDependency)){
    if(checkField('externalDependency',fieldsToCheck)){ return false; }
  }

  if(section.internalDependency && !isDependencyValid(false, section.internalDependency)){
    if(checkField('internalDependency',fieldsToCheck)){ return false; }
  }

  if(section.mergeMetaData && !isSectionMergeDataValid(section.mergeMetaData)){
    if(checkField('mergeMetaData',fieldsToCheck)){ return false; }
  }

  if(section.references && !areReferencesValid(section.references)){
    if(checkField('references',fieldsToCheck)){ return false; }
  }

  if(!section.questions || !areQuestionsValid(section.questions)){
    if(checkField('questions',fieldsToCheck)){ return false; }
  }

  if(!section.tasks || !areTasksValid(section.tasks)){
    if(checkField('tasks',fieldsToCheck)){ return false; }
  }

  return true;
}

const isValidCriteriaList = (elementList?: FbeCharacteristics[]): boolean => {
  if(!elementList || elementList.length ===0){ return true; }

  const invalid = elementList.filter(element => (
    !element.characteristicName || !element.characteristicValue
  ))

  return invalid.length === 0;
}

const isCriteriaValid = (criteria: FbeCriteria): boolean => {
  if(criteria.characteristics && (
      !isValidCriteriaList(criteria.characteristics.and) ||
      !isValidCriteriaList(criteria.characteristics.or)
  )){
    return false;
  }

  if(criteria.networkCharacteristics && (
    !isValidCriteriaList(criteria.networkCharacteristics.and) ||
    !isValidCriteriaList(criteria.networkCharacteristics.or)
  )){
    return false;
  }

  return true;
}

const isValidMapping = (mapping: ExternalDataMapping) : boolean => {

  if(!mapping.source ||
    !mapping.dest ||
    !mapping.type){
      return false;
  }
  return true;
}

const isExternalDataValid = (externalData?: ExternalData): boolean => {
  if(!externalData || externalData.mapping.length === 0){ return true; }

  const invalid = externalData.mapping.filter(element => !isValidMapping(element));
  return invalid.length === 0;
}

const isExternalDataListValid = (externalDataList?: ExternalData[]): boolean => {
  if(!externalDataList || externalDataList.length === 0){ return true; }

  const invalid = externalDataList.filter(externalData => !isExternalDataValid(externalData));

  return invalid.length === 0;
}

const isValidNumber = (value:number): boolean => {
  if(value === 0){ return true; }

  return !(!value || isNaN(value));
}

export const isFormValid = (form: FbeForm, fieldsToCheck: string[]): boolean => {
  if(!form){ return false };

//  if(!form.name || form.name.length === 0){ return true;} // first time

  if(form.active === undefined){
    if(checkField('active',fieldsToCheck)){ return false; }
  }

  if(!form.activeWorkflowStatus ||
    form.activeWorkflowStatus.length === 0){
    if(checkField('activeWorkflowStatus',fieldsToCheck)){ return false; }
  }

  if(!form.appliedCountries ||
    form.appliedCountries.length === 0){
    if(checkField('appliedCountries',fieldsToCheck)){ return false; }
  }

  if(!form.defaultLanguage){
    if(checkField('defaultLanguage',fieldsToCheck)){ return false; }
  }

  if(!form.criteria ||
    !isCriteriaValid(form.criteria)){
      if(checkField('criteria',fieldsToCheck)){ return false; }
  }

  if(!isExternalDataListValid(form.externalData)){
    if(checkField('externalData',fieldsToCheck)){ return false; }
  }

  if(!form.features){
      if(checkField('features',fieldsToCheck)){ return false; }
  }

  if(!isLocalisationValid(form.name)){
    if(checkField('name',fieldsToCheck)){ return false; }
  }

  if(!form.formId){
    if(checkField('formId',fieldsToCheck)){ return false; }
  }

  if(!isValidNumber(form.order)){
    if(checkField('order',fieldsToCheck)){ return false; }
  }

  if(!form.repeatType){
    if(checkField('repeatType',fieldsToCheck)){ return false; }
  }

  let temp = form.resultHandling ? form.resultHandling.filter(result => (!result.resultEmailReceivers || result.resultEmailReceivers.length === 0)) : [];
  if(temp.length > 0){
    if(checkField('resultEmailReceivers',fieldsToCheck)){ return false; }
  }

  temp = form.resultHandling ? form.resultHandling.filter(result => !result.resultWorkflowStatus || result.resultWorkflowStatus.length === 0) : [];
  if(temp.length > 0){
    if(checkField('resultWorkflowStatus',fieldsToCheck)){ return false; }
  }

  if(!form.role || form.role.length === 0){
    if(checkField('role',fieldsToCheck)){ return false; }
  }

  if(!form.templateVersion){
    if(checkField('templateVersion',fieldsToCheck)){ return false; }
  }

  if(!form.type){
    if(checkField('type',fieldsToCheck)){ return false; }
  }

  if(!form.workflowStatus){
    if(checkField('workflowStatus',fieldsToCheck)){ return false; }
  }

  return true;
}

export const deepEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) return true;

  if (typeof obj1 !== typeof obj2) return false;

  if (obj1 === null || obj2 === null) return obj1 === obj2;

  if (typeof obj1 !== 'object') return obj1 === obj2;

  if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;

  if (Array.isArray(obj1)) {
    if (obj1.length !== obj2.length) return false;
    return obj1.every((val, index) => deepEqual(val, obj2[index]));
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual(obj1[key], obj2[key])) return false;
  }

  return true;
}

/* Use below deepEqual if you need to find out where the difference is */
/*
// eslint-disable-next-line @typescript-eslint/no-inferrable-types
export const deepEqual = (obj1: any, obj2: any, path: string = ''): boolean => {
  if (obj1 === obj2) return true;

  if (typeof obj1 !== typeof obj2) {
    console.error(`Type mismatch at ${path || 'root'}:`, obj1, obj2);
    return false;
  }

  if (obj1 === null || obj2 === null) {
    if (obj1 !== obj2) {
      console.error(`Value mismatch at ${path || 'root'}:`, obj1, obj2);
    }
    return obj1 === obj2;
  }

  if (typeof obj1 !== 'object') {
    if (obj1 !== obj2) {
      console.error(`Value mismatch at ${path || 'root'}:`, obj1, obj2);
    }
    return obj1 === obj2;
  }

  if (Array.isArray(obj1) !== Array.isArray(obj2)) {
    console.error(`Array mismatch at ${path || 'root'}:`, obj1, obj2);
    return false;
  }

  if (Array.isArray(obj1)) {
    if (obj1.length !== obj2.length) {
      console.error(`Array length mismatch at ${path || 'root'}:`, obj1.length, obj2.length);
      return false;
    }
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEqual(obj1[i], obj2[i], `${path}[${i}]`)) return false;
    }
    return true;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    console.error(`obj1 ${JSON.stringify(obj1)}`);
    console.error(`obj2 ${JSON.stringify(obj2)}`);
    console.error(`keys1 ${JSON.stringify(keys1)}`);
    console.error(`keys2 ${JSON.stringify(keys2)}`);
    console.error(`Key count mismatch at ${path || 'root'}:`, keys1.length, keys2.length);
    return false;
  }

  for (const key of keys1) {
    if (!keys2.includes(key)) {
      console.error(`Missing key '${key}' in second object at ${path || 'root'}`);
      return false;
    }
    if (!deepEqual(obj1[key], obj2[key], path ? `${path}.${key}` : key)) {
      return false;
    }
  }

  return true;
};
*/

export const hasFormChanges = (originalForm: FbeForm, currentForm: FbeForm, compareFields: (keyof FbeForm)[]) : boolean => {
  if(!originalForm || !currentForm) { return true; }
  const changed = compareFields.filter(field => !deepEqual(originalForm[field], currentForm[field]))

  if(changed.length === 0){
    const originalLockedFields = (originalForm.lockedFields || []).filter(element => compareFields.includes(element as keyof FbeForm)).sort();
    const currentLockedFields = (currentForm.lockedFields || []).filter(element => compareFields.includes(element as keyof FbeForm)).sort();

    return !(
      originalLockedFields.every((value, index) => value === currentLockedFields[index]) &&
      originalLockedFields.length === currentLockedFields.length)
  }

  return changed.length > 0;
}

export const hasSectionChanges = (originalSection: FbeSection, currentSection: FbeSection, compareFields: (keyof FbeSection)[]) : boolean => {
  if(!originalSection || !currentSection) { return true; }
  const changed = compareFields.filter(field => !deepEqual(originalSection[field], currentSection[field]))

  if(changed.length === 0){
    const originalLockedFields = (originalSection.lockedFields || []).filter(element => compareFields.includes(element as keyof FbeSection)).sort();
    const currentLockedFields = (currentSection.lockedFields || []).filter(element => compareFields.includes(element as keyof FbeSection)).sort();
    return !(
      originalLockedFields.every((value, index) => value === currentLockedFields[index]) &&
      originalLockedFields.length === currentLockedFields.length);
  }

  return changed.length > 0;
}

export const hasQuestionChanges = (originalQuestion: FbeQuestion, currentQuestion: FbeQuestion, compareFields: (keyof FbeQuestion)[]) : boolean => {
  if(!originalQuestion || !currentQuestion) { return true; }
  const changed = compareFields.filter(field => !deepEqual(originalQuestion[field], currentQuestion[field]))

  if(changed.length === 0){
    const originalLockedFields = (originalQuestion.lockedFields || []).filter(element => compareFields.includes(element as keyof FbeQuestion)).sort();
    const currentLockedFields = (currentQuestion.lockedFields || []).filter(element => compareFields.includes(element as keyof FbeQuestion)).sort();
    return !(
      originalLockedFields.every((value, index) => value === currentLockedFields[index]) &&
      originalLockedFields.length === currentLockedFields.length);
  }

  return changed.length > 0;

}

export const getLocalizedText = (data: LocalisedText[], language: string): string => {
  let found = data.find(element => element.language === language);
  if(!found) { found = data.find(element => element.language); }
  if(! found && data.length > 0) { found = data[0]; }

  return found ? found.text : '';
}